import { FetchResult } from '@apollo/client'
import { css } from '@emotion/react'
import { Alert, Button, Cell, Grid, HFlow, Icon, Theme, Tooltip, useTheme } from 'bold-ui'
import { useAlert } from 'components/alert'
import { useErrorHandler } from 'components/error'
import { CpfField, Form, FormRenderProps, MunicipioSelectModel, SelectField, TextField } from 'components/form'
import { CnpjField } from 'components/form/field/CnpjField'
import { PerfilSelectModel } from 'components/form/field/select/PerfilSelectField'
import { TipoPessoaGroupField } from 'components/form/field/TipoPessoaRadioGroupField'
import { PopperButton, PopperControls } from 'components/popper/PopperButton'
import { FormApi } from 'final-form'
import { useSalvarSistemaExternoMutation } from 'graphql/hooks.generated'
import { SalvarSistemaExternoMutation, TipoPessoaEnum, TipoSistemaExternoEnum } from 'graphql/types.generated'
import { useRef, useState } from 'react'
import { isHttpsConnection } from 'util/https'
import { metaPath } from 'util/metaPath'
import { cnpj, cpf, createValidator, email, maxLength, minLength, nome, required, validate } from 'util/validation'

import { ChaveAssinatura, ChaveAssinaturaGeradaModal } from './ChaveAssinaturaGeradaModal'
import { TIPOS_SISTEMA_EXTERNO, tipoSistemaExternoToLabel } from './model-sistema-externo'
import { RestricoesFormModel, SistemasExternosRestricoesPopperForm } from './SistemasExternosRestricoesPopperForm'
import { SistemasExternosTermoModal } from './SistemasExternosTermoModal'

interface SistemaExternoFormModel {
  nome: string
  tipo: TipoSistemaExternoEnum
  url: string
  nomeResponsavel: string
  tipoPessoa: TipoPessoaEnum
  cpfCnpj: string
  email: string
  municipiosVinculados: MunicipioSelectModel[]
  perfisVinculados: PerfilSelectModel[]
}

const path = metaPath<SistemaExternoFormModel>()

const FORM_INITIAL_VALUES = {
  tipo: null,
  tipoPessoa: null,
  municipiosVinculados: [],
  perfisVinculados: [],
}

const validator = createValidator<SistemaExternoFormModel>(
  {
    nome: [required, minLength(3), maxLength(100)],
    tipo: [required],
    url: [required, minLength(12), maxLength(2048)],
    nomeResponsavel: [required, nome()],
    email: [required, email, minLength(6), maxLength(100)],
  },
  (values, errors) => {
    if (!errors.url && !values.url.toLowerCase().startsWith('https://')) {
      errors.url = 'A URL deve começar com https://'
      return errors
    }

    errors.tipoPessoa = validate(values.tipoPessoa, required)

    if (errors.tipoPessoa) return errors

    errors.cpfCnpj = validate(values.cpfCnpj, required)

    if (errors.cpfCnpj) return errors

    if (values.tipoPessoa === TipoPessoaEnum.FISICA) {
      errors.cpfCnpj = errors.cpfCnpj ?? validate(values.cpfCnpj, cpf)
    }

    if (values.tipoPessoa === TipoPessoaEnum.JURIDICA) {
      errors.cpfCnpj = errors.cpfCnpj ?? validate(values.cpfCnpj, cnpj)
    }

    return errors
  }
)

interface SistemasExternosFormProps {
  readonly refetch: () => void
}

export function SistemasExternosForm({ refetch }: SistemasExternosFormProps) {
  const isFormEnabled = isHttpsConnection
  const [salvarSistemaExterno] = useSalvarSistemaExternoMutation()
  const alert = useAlert()
  const handleRejection = useErrorHandler()

  const theme = useTheme()
  const styles = createStyles(theme)

  const [isModalChaveAssinaturaOpen, setIsModalChaveAssinaturaOpen] = useState(false)
  const [isTermoModalOpen, setIsTermoModalOpen] = useState(false)
  const [chaveAssinatura, setChaveAssinatura] = useState<ChaveAssinatura>()
  const formRef = useRef<FormApi<SistemaExternoFormModel>>()

  const onSubmitSuccess = (
    payload: FetchResult<SalvarSistemaExternoMutation>,
    form: FormApi<SistemaExternoFormModel>
  ) => {
    refetch()
    alert('success', 'Sistema externo cadastrado com sucesso.')
    setChaveAssinatura({ chave: payload.data?.salvarSistemaExterno.chaveAssinatura })
    setIsModalChaveAssinaturaOpen(true)
    setTimeout(() => {
      form.reset()
      form.getRegisteredFields().forEach((field) => form.resetFieldState(field))
    })
  }

  const handleSubmit = (values: SistemaExternoFormModel, form: FormApi<SistemaExternoFormModel>) => {
    if (!isFormEnabled) {
      return
    }

    const { municipiosVinculados, perfisVinculados, ...rest } = values
    const input = {
      ...rest,
      municipioIds: municipiosVinculados?.map((m) => m.id) ?? [],
      perfilIds: perfisVinculados?.map((p) => p.id) ?? [],
    }

    salvarSistemaExterno({
      variables: { input },
    })
      .then((result) => {
        onSubmitSuccess(result, form)
      })
      .catch(handleRejection)
  }

  const handleCloseChaveAssinaturaModal = () => {
    setIsModalChaveAssinaturaOpen(false)
    setChaveAssinatura(null)
  }

  const renderForm = (renderProps: FormRenderProps<Partial<SistemaExternoFormModel>>) => {
    formRef.current = renderProps.form as FormApi<SistemaExternoFormModel>

    const onReset = () => {
      renderProps.form.reset()
      renderProps.form.getRegisteredFields().forEach((field) => renderProps.form.resetFieldState(field))
    }

    const isPessoaFisica = renderProps.values.tipoPessoa === TipoPessoaEnum.FISICA
    const isAtendimento = renderProps.values.tipo === TipoSistemaExternoEnum.ATENDIMENTO

    const handleRestricoesChange = (restricoes: RestricoesFormModel) => {
      renderProps.form.change('municipiosVinculados', restricoes.municipiosVinculados)
      renderProps.form.change('perfisVinculados', restricoes.perfisVinculados)
    }

    const handleRestricoesClear = () => {
      handleRestricoesChange({ municipiosVinculados: [], perfisVinculados: [] })
    }

    const countRestricoes =
      (renderProps.values.municipiosVinculados?.length ?? 0) + (renderProps.values.perfisVinculados?.length ?? 0)

    const buttonLabel = `Adicionar restrições (${countRestricoes})`

    const renderRestricoesPopper = (ctrl: PopperControls) => (
      <SistemasExternosRestricoesPopperForm
        initialValues={{
          municipiosVinculados: renderProps.values.municipiosVinculados ?? [],
          perfisVinculados: renderProps.values.perfisVinculados ?? [],
        }}
        onSubmit={handleRestricoesChange}
        onFechar={ctrl.close}
        onClear={handleRestricoesClear}
      />
    )

    const handleOpenTermoModal = () => {
      if (!isFormEnabled) {
        return
      }

      const errors = validator(renderProps.values as SistemaExternoFormModel)
      const hasErrors = errors && Object.keys(errors).some((key) => errors[key])

      if (hasErrors) {
        renderProps.handleSubmit()
        return
      }

      setIsTermoModalOpen(true)
    }

    return (
      <form noValidate>
        <Grid>
          <Cell size={4} alignSelf='flex-start'>
            <TextField name={path.nome} label='Nome do sistema' required />
          </Cell>
          <Cell size={8}>
            <Alert type='info' inline style={{ marginTop: '1.5rem', marginLeft: '-1rem' }}>
              Este nome será visível por todos os profissionais com acesso ao sistema externo.
            </Alert>
          </Cell>
          <Cell size={4} alignSelf='flex-start'>
            <SelectField
              name={path.tipo}
              label='Local de acesso'
              required
              items={TIPOS_SISTEMA_EXTERNO}
              itemToString={tipoSistemaExternoToLabel}
            />
          </Cell>
          <Cell size={8} />
          <Cell size={12}>
            <PopperButton
              kind='primary'
              size='small'
              skin='outline'
              placement='bottom-start'
              focusOnOpen={false}
              renderPopper={renderRestricoesPopper}
            >
              <Icon icon='plus' style={{ marginRight: '0.5rem' }} />
              {buttonLabel}
            </PopperButton>
          </Cell>
          <Cell size={12} alignSelf='flex-start'>
            <TextField name={path.url} label='URL' required />
          </Cell>
          {renderProps.values.tipo && (
            <Cell size={12} alignSelf='flex-start'>
              <Alert type='info' inline style={{ width: '100%' }}>
                {isAtendimento
                  ? 'Os termos [CNES], [documentoCidadao] e [documentoProfissional] quando escritos entre colchetes, serão substituídos pelos dados dentro do contexto do atendimento.'
                  : 'Os termos [CNES] e [documentoProfissional] quando escritos entre colchetes, serão substituídos pelos dados do profissional.'}
              </Alert>
            </Cell>
          )}
          <Cell size={12}>
            <TipoPessoaGroupField name={path.tipoPessoa} required label='Cadastrar sistema para' />
          </Cell>
          <Cell size={4}>
            <TextField name={path.nomeResponsavel} label='Responsável' required />
          </Cell>
          <Cell size={3}>
            {isPessoaFisica ? (
              <CpfField name={path.cpfCnpj} label='CPF' required />
            ) : (
              <CnpjField name={path.cpfCnpj} label='CNPJ' required />
            )}
          </Cell>
          <Cell size={5} />
          <Cell size={4}>
            <TextField name={path.email} label='E-mail' required />
          </Cell>
          <Cell size={8} />
          <Cell size={12}>
            <HFlow justifyContent='flex-end'>
              <Button size='small' onClick={onReset}>
                Limpar campos
              </Button>

              <Tooltip
                text={
                  !isHttpsConnection &&
                  'Para cadastrar sistema externo é necessário que a instalação possua HTTPS configurado.'
                }
              >
                <Button kind='primary' size='small' onClick={handleOpenTermoModal} disabled={!isHttpsConnection}>
                  Cadastrar sistema
                </Button>
              </Tooltip>
            </HFlow>
          </Cell>
        </Grid>
      </form>
    )
  }

  const handleCloseTermoModal = () => {
    setIsTermoModalOpen(false)
  }

  const handleConfirmTermo = () => {
    if (formRef.current) {
      formRef.current.submit()
    }
  }

  return (
    <div css={styles.box}>
      <ChaveAssinaturaGeradaModal
        chaveAssinatura={chaveAssinatura}
        isOpen={isModalChaveAssinaturaOpen}
        onClose={handleCloseChaveAssinaturaModal}
      />
      <SistemasExternosTermoModal
        isOpen={isTermoModalOpen}
        onClose={handleCloseTermoModal}
        onConfirm={handleConfirmTermo}
      />
      <Form render={renderForm} validate={validator} onSubmit={handleSubmit} initialValues={FORM_INITIAL_VALUES} />
    </div>
  )
}

const createStyles = (theme: Theme) => ({
  box: css`
    padding: 1rem;
    border: 1px solid ${theme.pallete.divider};
  `,
})
